import {Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import {Router} from '@angular/router';
import {ConfiguratorI18nService, DatastoreDto, I18n, I18nString} from '@tj/tj-dmp-lib-api';
import {ConfirmationService, LazyLoadEvent, MenuItem, MessageService} from 'primeng/api';
import {TranslateService} from '@ngx-translate/core';

@Component({
    templateUrl: './etl-dictionnary.component.html',
    styleUrls: ['./etl-dictionnary.component.scss'],
    standalone: false
})
export class EtlDictionnaryComponent implements OnInit {
    breadcrumb = [
        {label: this.translate.instant('menu.flow.label')},
        {label: this.translate.instant('menu.flow.dictionary')}
    ];
    description = this.translate.instant('etl.dictionary.description');

    datastore: DatastoreDto;

    datas: I18nString[] = [];
    selectedI18ns: I18nString[] = [];
    totalRecords: number;

    actualEvent: LazyLoadEvent = {
        first: 0,
        rows: 10
    };

    state: { message: string };

    langs = ['FR', 'EN', 'IT', 'DE', 'NL', 'ES'] as const;
    modalType: 'create' | 'edit' = null;
    actualIndex: number = null;
    actual: I18nString;

    uploadingFile = false;
    exportMenuItems: MenuItem[] = [];

    @ViewChild('i18nFileInput') i18nFileInput: ElementRef;

    constructor(private confirmationService: ConfirmationService,
                private i18nService: ConfiguratorI18nService,
                private messageService: MessageService,
                private router: Router,
                private readonly translate: TranslateService) {
        const navigation = this.router.getCurrentNavigation();
        this.state = navigation?.extras?.state as { message: string } | undefined;
        this.translate.onLangChange.subscribe(() => this.rebuildTranslatedHeader());
    }

    private rebuildTranslatedHeader(): void {
        this.breadcrumb = [
            {label: this.translate.instant('menu.flow.label')},
            {label: this.translate.instant('menu.flow.dictionary')}
        ];
        this.description = this.translate.instant('etl.dictionary.description');
    }

    ngOnInit(): void {
        if (this.state?.message === 'success_created') {
            setTimeout(() => this.messageService.add({severity: 'success', summary: this.translate.instant('datastore.successSummary'), detail: this.translate.instant('etl.dictionary.entryCreatedDetail')}));
        } else if (this.state?.message === 'success_updated') {
            setTimeout(() => this.messageService.add({
                severity: 'success',
                summary: this.translate.instant('datastore.successSummary'),
                detail: this.translate.instant('etl.dictionary.entryUpdatedDetail')
            }));
        }
        this.initExportMenu();
    }

    /**
     * Initialise le menu d'export/import
     */
    private initExportMenu() {
        this.exportMenuItems = [
            {
                label: this.translate.instant('subscriptions.importFile'),
                icon: 'fas fa-upload',
                command: () => this.triggerFileUpload()
            },
            {
                label: this.translate.instant('subscriptions.exportSelection'),
                icon: 'fas fa-file-export',
                command: () => this.exportSelectedI18ns(),
                disabled: !this.selectedI18ns || this.selectedI18ns.length === 0
            },
            {
                label: this.translate.instant('subscriptions.exportAll'),
                icon: 'fas fa-download',
                command: () => this.exportAllI18ns(),
                disabled: !this.datas || this.datas.length === 0
            }
        ];
    }

    loadLazy(event: LazyLoadEvent = null) {
        if (event) {
            this.actualEvent = event;
        }
        const start = Math.round(this.actualEvent.first / this.actualEvent.rows);
        const end = this.actualEvent.rows;
        this.i18nService.getAllI18ns(this.datastore?.code, {page: start, size: end, sort: []}, 'response')
            .subscribe((res) => {
                this.totalRecords = +res.headers.get('x-total-count');
                this.datas = res.body as Array<I18nString>;
                this.updateExportMenuState();
        });
    }

    remove(item: I18nString) {
        this.confirmationService.confirm({
            message: this.translate.instant('etl.dictionary.confirmDeleteMessage', {key: item.key}),
            accept: () => {
                this.i18nService.deleteI18nUsingDELETE(this.datastore.code, item.id).subscribe(() => {
                    this.loadLazy();
                    this.messageService.add({
                        severity: 'success',
                        summary: this.translate.instant('datastore.successSummary'),
                        detail: this.translate.instant('etl.dictionary.deleteSuccessDetail')
                    });
                }, () => {
                    this.messageService.add({
                        severity: 'error',
                        summary: this.translate.instant('datastore.errorSummary'),
                        detail: this.translate.instant('landingPage.template.genericErrorDetail')
                    });
                });
            }
        });
    }

    create() {
        this.modalType = 'create';
        this.actual = {
            group: 'common',
            type: 'string',
            key: '',
            dictionary: this.langs.reduce((acc, curr) => ({...acc, [curr]: ''}), {})
        };
    }
    edit(element: I18nString, index: number) {
        this.modalType = 'edit';
        this.actualIndex = index;
        this.actual = {
            id: element.id,
            group: element.group,
            type: element.type,
            key: element.key,
            dictionary: {...element.dictionary}
        };
    }

    save() {
        if (this.modalType === 'create') {
            this.i18nService.createI18nUsingPOST(this.datastore?.code, this.actual).subscribe(res => this.callback(res));
        } else if (this.modalType === 'edit') {
            this.i18nService.updateI18nUsingPUT(this.datastore?.code, this.actual).subscribe(res => this.callback(res));
        }
    }

    private callback(res: I18n) {
        this.actual = null;
        this.modalType = null;
        this.messageService.add({
            severity: 'success',
            summary: this.translate.instant('datastore.successSummary')
        });
        this.loadLazy();
    }

    updateTenant($event: DatastoreDto) {
        this.datastore = $event;
        this.loadLazy();
    }

    /**
     * Déclenche l'ouverture du sélecteur de fichier
     */
    triggerFileUpload() {
        this.i18nFileInput.nativeElement.click();
    }

    /**
     * Gère l'import de fichiers (JSON ou ZIP)
     */
    importFile(event: any) {
        const files: FileList = event.target.files;
        if (!files || files.length === 0) {
            return;
        }

        this.uploadingFile = true;
        const file: File = files[0];

        if (file.name.endsWith('.json')) {
            this.handleJsonImport(file);
        } else if (file.name.endsWith('.zip')) {
            this.handleZipImport(file);
        } else {
            this.messageService.add({
                severity: 'error',
                summary: this.translate.instant('datastore.errorSummary'),
                detail: this.translate.instant('subscriptions.unsupportedFormatDetail')
            });
            this.uploadingFile = false;
        }

        // Reset input
        event.target.value = '';
    }

    /**
     * Importe un fichier JSON
     */
    private handleJsonImport(file: File) {
        const reader: FileReader = new FileReader();
        reader.onload = (e: any) => {
            const jsonContent: string = e.target.result;
            this.i18nService.importI18nJson(this.datastore.code, jsonContent).subscribe(
                (entity: I18n) => {
                    this.uploadingFile = false;
                    this.messageService.add({
                        severity: 'success',
                        summary: this.translate.instant('datastore.successSummary'),
                        detail: this.translate.instant('etl.dictionary.importSuccessDetail')
                    });
                    this.loadLazy();
                },
                (error) => {
                    this.uploadingFile = false;
                    this.messageService.add({
                        severity: 'error',
                        summary: this.translate.instant('datastore.errorSummary'),
                        detail: error.error?.detail || this.translate.instant('subscriptions.importErrorDetail')
                    });
                }
            );
        };
        reader.readAsText(file);
    }

    /**
     * Importe un fichier ZIP
     */
    private handleZipImport(file: File) {
        this.i18nService.importI18nsZip(this.datastore.code, file).subscribe(
            (entities: I18n[]) => {
                this.uploadingFile = false;
                this.messageService.add({
                    severity: 'success',
                    summary: this.translate.instant('datastore.successSummary'),
                    detail: this.translate.instant('etl.dictionary.importSuccessMultipleDetail', {count: entities.length})
                });
                this.loadLazy();
            },
            (error) => {
                this.uploadingFile = false;
                this.messageService.add({
                    severity: 'error',
                    summary: this.translate.instant('datastore.errorSummary'),
                    detail: error.error?.detail || this.translate.instant('subscriptions.importErrorDetail')
                });
            }
        );
    }

    /**
     * Exporte une seule entrée en JSON
     */
    exportI18nJson(entity: I18nString) {
        this.i18nService.exportI18nJson(this.datastore.code, entity.id).subscribe(
            (blob: Blob) => {
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `i18n-${entity.key}.json`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);

                this.messageService.add({
                    severity: 'success',
                    summary: this.translate.instant('datastore.successSummary'),
                    detail: this.translate.instant('etl.dictionary.exportSuccessDetail')
                });
            },
            (error) => {
                this.messageService.add({
                    severity: 'error',
                    summary: this.translate.instant('datastore.errorSummary'),
                    detail: this.translate.instant('subscriptions.exportErrorDetail')
                });
            }
        );
    }

    /**
     * Exporte plusieurs entrées en ZIP
     */
    exportSelectedI18ns() {
        if (!this.selectedI18ns || this.selectedI18ns.length === 0) {
            this.messageService.add({
                severity: 'warn',
                summary: this.translate.instant('datastore.warnSummary'),
                detail: this.translate.instant('etl.dictionary.noEntrySelectedDetail')
            });
            return;
        }

        const ids = this.selectedI18ns.map(c => c.id);
        this.i18nService.exportI18nsZip(this.datastore.code, ids).subscribe(
            (blob: Blob) => {
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `i18ns-export.zip`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);

                this.messageService.add({
                    severity: 'success',
                    summary: this.translate.instant('datastore.successSummary'),
                    detail: this.translate.instant('etl.dictionary.exportSuccessMultipleDetail', {count: this.selectedI18ns.length})
                });
            },
            (error) => {
                this.messageService.add({
                    severity: 'error',
                    summary: this.translate.instant('datastore.errorSummary'),
                    detail: this.translate.instant('subscriptions.exportErrorDetail')
                });
            }
        );
    }

    /**
     * Exporte toutes les entrées en ZIP
     */
    exportAllI18ns() {
        if (!this.datas || this.datas.length === 0) {
            this.messageService.add({
                severity: 'warn',
                summary: this.translate.instant('datastore.warnSummary'),
                detail: this.translate.instant('etl.dictionary.noEntryToExportDetail')
            });
            return;
        }

        const ids = this.datas.map(c => c.id);
        this.i18nService.exportI18nsZip(this.datastore.code, ids).subscribe(
            (blob: Blob) => {
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `i18ns-export-all.zip`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);

                this.messageService.add({
                    severity: 'success',
                    summary: this.translate.instant('datastore.successSummary'),
                    detail: this.translate.instant('etl.dictionary.exportSuccessMultipleDetail', {count: this.datas.length})
                });
            },
            (error) => {
                this.messageService.add({
                    severity: 'error',
                    summary: this.translate.instant('datastore.errorSummary'),
                    detail: this.translate.instant('subscriptions.exportErrorDetail')
                });
            }
        );
    }

    /**
     * Met à jour l'état des items du menu export
     */
    private updateExportMenuState() {
        if (this.exportMenuItems && this.exportMenuItems.length > 0) {
            // Update "Exporter la sélection" (index 1)
            if (this.exportMenuItems[1]) {
                this.exportMenuItems[1].disabled = !this.selectedI18ns || this.selectedI18ns.length === 0;
            }
            // Update "Exporter tout" (index 2)
            if (this.exportMenuItems[2]) {
                this.exportMenuItems[2].disabled = !this.datas || this.datas.length === 0;
            }
        }
    }

    onSelectionChange() {
        this.updateExportMenuState();
    }
}
